import { cn } from '../../../shared/lib/utils';
import { ChevronRight } from 'lucide-react';

interface NavItem { label: string; href: string }

interface ARGroup {
  name: string;
  hubHref: string;
  items: NavItem[];
}

const AR_GROUPS: ARGroup[] = [
  {
    name: 'Customers',
    hubHref: '#ar-customers',
    items: [
      { label: 'Customer Management', href: '#customer-mgmt' },
      { label: 'Communications', href: '#ar-communications' },
      { label: 'Reminders', href: '#ar-reminders' },
      { label: 'Customer Portal', href: '#ar-customer-portal' },
    ],
  },
  {
    name: 'Invoicing',
    hubHref: '#ar-invoicing',
    items: [
      { label: 'Invoices', href: '#invoicing' },
      { label: 'Recurring Invoices', href: '#ar-recurring-invoices' },
      { label: 'Invoice Templates', href: '#ar-templates' },
      { label: 'Estimates & Quotes', href: '#ar-estimates' },
      { label: 'Revenue Recognition', href: '#ar-rev-recognition' },
    ],
  },
  {
    name: 'Payments',
    hubHref: '#ar-payments',
    items: [
      { label: 'Payment Recording', href: '#ar-payment-recording' },
      { label: 'Payment Plans', href: '#ar-payment-plans' },
      { label: 'Online Payments', href: '#ar-online-payments' },
    ],
  },
  {
    name: 'Credit & Collections',
    hubHref: '#ar-credit',
    items: [
      { label: 'Credit Management', href: '#credit-mgmt' },
      { label: 'Credit Exposure', href: '#ar-credit-exposure' },
      { label: 'AI Collections', href: '#ar-ai-collections' },
      { label: 'Collections', href: '#ar-collections' },
      { label: 'Dunning Letters', href: '#ar-dunning' },
    ],
  },
  {
    name: 'Reports & Aging',
    hubHref: '#ar-reports',
    items: [
      { label: 'AR Aging', href: '#aging-reports' },
      { label: 'Customer Statements', href: '#ar-statements' },
      { label: 'Revenue Reports', href: '#ar-revenue-reports' },
      { label: 'Bank Reconciliation', href: '#ar-bank-recon' },
      { label: 'Consolidated AR', href: '#ar-consolidated' },
    ],
  },
];

/**
 * Find which group the current page belongs to
 */
function findGroup(currentPath: string): ARGroup | null {
  return AR_GROUPS.find(g => g.hubHref === currentPath || g.items.some(i => i.href === currentPath)) || null;
}

/**
 * AR Sub-Navigation — shows breadcrumb + sibling tabs
 * Place this at the top of any AR page.
 */
export default function ARSubNav() {
  const currentPath = window.location.hash.split('?')[0] || '#';
  const group = findGroup(currentPath);
  if (!group) return null;

  const currentItem = group.items.find(i => i.href === currentPath);
  const activeHref = currentItem ? currentPath : group.items[0]?.href;

  return (
    <div className="shrink-0 sticky top-0 z-30 border-b border-slate-200 dark:border-slate-800 bg-white/95 dark:bg-slate-900/95 backdrop-blur-sm px-4">
      <div className="flex items-center gap-4 -mb-px overflow-x-auto mobile-tabs-scroll">
        {/* Breadcrumb */}
        <div className="flex items-center gap-1.5 shrink-0 py-2.5">
          <a href={group.hubHref} className="text-[10px] font-bold text-slate-400 hover:text-amber-500 transition-colors">AR</a>
          <ChevronRight className="w-3 h-3 text-slate-300" />
          <span className="text-[10px] font-bold text-slate-500 dark:text-slate-400">{group.name}</span>
        </div>

        <div className="w-px h-5 bg-slate-200 dark:bg-slate-700 shrink-0" />

        {/* Sibling Tabs */}
        <div className="flex items-center gap-0.5 shrink-0">
          {group.items.map(item => {
            const isActive = item.href === activeHref;
            return (
              <a key={item.href} href={item.href}
                className={cn(
                  "px-3 py-2.5 text-[11px] font-bold transition-all border-b-2 whitespace-nowrap",
                  isActive
                    ? "text-amber-600 dark:text-amber-400 border-amber-500"
                    : "text-slate-400 border-transparent hover:text-slate-600 dark:hover:text-slate-300 hover:border-slate-300 dark:hover:border-slate-600"
                )}>
                {item.label}
              </a>
            );
          })}
        </div>
      </div>
    </div>
  );
}
